import React from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { SmartLinksAnalyticsContextLocation } from '../../ai-answer-dialog/analytics';
import {
	type NLPSearchWhoResultCollaboratorDataType,
	type NLPSearchWorkResultRecentActivityDataType,
} from '../../ai-answer-dialog/types';
import {
	SainAction,
	useAnalytics,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics';
import { messages } from '../../messages';
import { onKnowledgeCardLinkClick } from '../analytics';
import { KnowledgeCardActivityRow } from '../common/knowledge-card-activity-row';
import {
	type ActivityColumnConfig,
	KnowledgeCardBody,
	type UserColumnConfig,
} from '../common/knowledge-card-body';
import { KnowledgeCardUserRow } from '../common/knowledge-card-user-row';
import { KnowledgeCardsIntentType } from '../types';
import { useTeamsAppLinks } from '../use-teams-app-links';

type KnowledgeCardsBodyProps = {
	currentUserId: string;
	recommendedUsers: NLPSearchWhoResultCollaboratorDataType[];
	recentActivity: NLPSearchWorkResultRecentActivityDataType[];
	profileUrl?: string;
	viewAllUrl?: string;
	teamId?: string;
};

export const TeamCardBody = React.memo(
	({
		currentUserId,
		recommendedUsers,
		recentActivity,
		profileUrl,
		viewAllUrl,
		teamId,
	}: KnowledgeCardsBodyProps) => {
		const { commonAttributes } = useAIAnswerContext();
		const [fireAnalyticsEvent] = useAnalytics();
		const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
		const { formatMessage } = useIntl();
		const { viewAllLink, viewAllOnNavigate, profileHref } = useTeamsAppLinks({
			type: KnowledgeCardsIntentType.TEAM,
			currentUserId,
			viewAllUrl,
			cloudId: commonAttributes.cloudId,
			teamId,
		});

		const handleRecentWorkViewAllClick = (e: React.MouseEvent) => {
			fireAnalyticsEvent(
				onKnowledgeCardLinkClick({ ...commonAttributes, location: 'recent-work-view-all' }),
			);
			fireSAINUsageEvent({ metCriteria: SainAction.RECENT_WORK_CLICKED });
			if (fg('platform-adopt-teams-nav-config')) {
				viewAllOnNavigate(e);
			}
		};

		const handleTeamMembersViewAllClick = (e: React.MouseEvent) => {
			fireAnalyticsEvent(
				onKnowledgeCardLinkClick({ ...commonAttributes, location: 'team-members-view-all' }),
			);
			fireSAINUsageEvent({ metCriteria: SainAction.TEAM_MEMBER_CLICKED });
			if (fg('platform-adopt-teams-nav-config')) {
				viewAllOnNavigate(e);
			}
		};

		// Determine which columns to render
		const hasRecentActivity = recentActivity.length > 0;
		const hasTeamMembers = recommendedUsers.length > 0;

		const columnsConfig = [
			// Recent Activity Column
			...(hasRecentActivity
				? [
						{
							viewAllLinkOnClick: handleRecentWorkViewAllClick,
							viewAllLinkHref: viewAllLink,
							data: recentActivity,
							component: KnowledgeCardActivityRow,
							header: formatMessage(messages.knowledge_cards_recent_work_subheader),
							onClickCallback: (position: number, shownAfterCardBodyExpanded: boolean) => {
								fireAnalyticsEvent(
									onKnowledgeCardLinkClick({
										...commonAttributes,
										location: 'recent-work-item',
										position,
										shownAfterCardBodyExpanded,
									}),
								);
								fireSAINUsageEvent({ metCriteria: SainAction.RECENT_WORK_CLICKED });
							},
							smartLinksAnalyticsContextLocation: SmartLinksAnalyticsContextLocation.SAIN_TEAM_CARD,
						} as unknown as ActivityColumnConfig,
					]
				: []),
			// Team Members Column
			...(hasTeamMembers
				? [
						{
							viewAllLinkOnClick: handleTeamMembersViewAllClick,
							viewAllLinkHref: viewAllLink,
							data: recommendedUsers,
							component: KnowledgeCardUserRow,
							onClickCallback: (position: number, shownAfterCardBodyExpanded: boolean) => {
								fireAnalyticsEvent(
									onKnowledgeCardLinkClick({
										...commonAttributes,
										location: 'user-list-item',
										position,
										shownAfterCardBodyExpanded,
									}),
								);
								fireSAINUsageEvent({ metCriteria: SainAction.TEAM_MEMBER_CLICKED });
							},
							header: formatMessage(messages.knowledge_cards_team_members_subheader),
						} as unknown as UserColumnConfig,
					]
				: []),
		];

		return (
			<KnowledgeCardBody
				columnsConfig={columnsConfig}
				profileUrl={fg('platform-adopt-teams-nav-config') ? profileHref : profileUrl}
				footerButtonText={formatMessage(messages.knowledge_cards_view_team)}
				footerButtonAction={SainAction.VIEW_TEAM_PAGE_CLICKED}
			/>
		);
	},
);
